/* eslint-disable no-param-reassign */
import {
  AuthSession,
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
} from 'aws-amplify/auth';
import {
  createListenerMiddleware,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { appSyncGraphQLApi } from '../../app/smorgAppSyncGraphQLApi';
import { postMessageToSmorgCompanion } from '../../services/smorg_companion_auth';

export interface UserIdState {
  username: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  groups: Array<string>;
  isLoading: boolean;
  appLoaded: boolean;
}

export interface UserInfo {
  username: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  groups: Array<string>;
}

const initialState: UserIdState = {
  username: null,
  email: null,
  givenName: null,
  familyName: null,
  groups: [],
  isLoading: true,
  appLoaded: false,
};

export const userIdSlice = createSlice({
  name: 'userId',
  initialState,
  selectors: {
    currentUserSelector: createSelector(
      [
        (state: UserIdState) => state.isLoading,
        (state: UserIdState) => state.username,
        (state: UserIdState) => state.email,
        (state: UserIdState) => state.givenName,
        (state: UserIdState) => state.familyName,
        (state: UserIdState) => state.groups,
      ],
      (isLoading, username, email, givenName, familyName, groups) => {
        return {
          username,
          email,
          givenName,
          familyName,
          groups,
          isLoading,
        };
      },
    ),
    userFriendlyNameForActivitySignalSelector: (state: UserIdState) => {
      if (state.givenName && state.familyName) {
        return `${state.givenName} ${state.familyName}`;
      }
      if (state.givenName) {
        return state.givenName;
      }
      if (state.email) {
        return state.email;
      }
      return null;
    },
    userDefaultFriendlyNameForChatSelector: (state: UserIdState) => {
      return `${state.givenName || ''} ${state.familyName || ''}`;
    },
  },
  reducers: {
    appLoadedAction: (state) => {
      state.appLoaded = true;
      state.isLoading = true;
    },
    userAvailable: (state, action: PayloadAction<UserInfo>) => {
      state.username = action.payload.username;
      state.email = action.payload.email;
      state.givenName = action.payload.givenName;
      state.familyName = action.payload.familyName;
      state.groups = action.payload.groups;
      state.isLoading = false;
    },
    noUserLoggedIn: (state) => {
      state.isLoading = false;
    },
    loginStarted: (state) => {
      state.isLoading = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userIdSlice.actions.appLoadedAction, () => {
        console.log('appLoadedAction');
      })
      .addCase(appSyncGraphQLApi.util.resetApiState, (state) => {
        state.username = null;
        state.email = null;
        state.givenName = null;
        state.familyName = null;
        state.groups = [];
        state.isLoading = true;
        state.appLoaded = false;
      });
  },
});

export const {
  currentUserSelector,
  userFriendlyNameForActivitySignalSelector,
  userDefaultFriendlyNameForChatSelector,
} = userIdSlice.selectors;

export const { appLoadedAction, loginStarted } = userIdSlice.actions;

export const userIdListenerMiddleware = createListenerMiddleware();

export const dispatchAmplifyUser = async (dispatch: any) => {
  const promises = [
    getCurrentUser().catch(() => null),
    fetchAuthSession(),
    fetchUserAttributes().catch(() => null),
  ];
  try {
    const results = await Promise.all(promises);
    const currentUser = results[0] as AuthUser;
    const session = results[1] as AuthSession;
    const attributes = results[2] as FetchUserAttributesOutput;
    const username = currentUser?.username || null;
    if (username) {
      let groups: Array<string> = [];
      if (session.tokens) {
        const memberInCognitoGroups =
          (session.tokens.accessToken.payload[
            'cognito:groups'
          ] as Array<string>) || [];
        groups = memberInCognitoGroups;
      }
      let email: string | null = null;
      let givenName: string | null = null;
      let familyName: string | null = null;
      if (attributes) {
        email = attributes.email || null;
        givenName = attributes.given_name || null;
        familyName = attributes.family_name || null;
      }
      dispatch(
        userIdSlice.actions.userAvailable({
          username,
          email,
          givenName,
          familyName,
          groups,
        }),
      );
    } else {
      console.log('No user logged in');
      dispatch(userIdSlice.actions.noUserLoggedIn());
    }
  } catch (error) {
    console.error('Error fetching user or session:', error);
    dispatch(userIdSlice.actions.noUserLoggedIn());
  }
};

userIdListenerMiddleware.startListening({
  actionCreator: appLoadedAction,
  effect: async (action, listenerApi) => {
    console.log('userIdListenerMiddleware: appLoadedAction');
    const returnValue = await dispatchAmplifyUser(listenerApi.dispatch);
    const loggedInUser = (listenerApi.getState() as any).userId.username;
    console.log(
      `userIdListenerMiddleware: loggedInUser: ${JSON.stringify(loggedInUser)}`,
    );
    if (loggedInUser) {
      console.log(
        `User already logged in when appLoadedAction called, triggering askForAppStoreReview`,
      );
      postMessageToSmorgCompanion({
        type: 'askForAppStoreReview',
      });
    } else {
      console.log(
        `No user logged in when appLoadedAction called, skipping native app review dialog`,
      );
    }
    return returnValue;
  },
});

export default userIdSlice.reducer;
